import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Link } from "raviger";
import { useState } from "react";

import { EditInvoiceDialog } from "@/components/Billing/Invoice/EditInvoiceDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MonetaryDisplay } from "@/components/ui/monetary-display";

import { ChargeItemServiceResource } from "@/types/billing/chargeItem/chargeItem";
import chargeItemApi from "@/types/billing/chargeItem/chargeItemApi";
import { InvoiceStatus } from "@/types/billing/invoice/invoice";
import { FacilityRead } from "@/types/facility/facility";
import query from "@/Utils/request/query";

interface RegistrationChargeStatusCardProps {
  facility: FacilityRead;
  facilityId: string;
  patientId: string;
}

export default function RegistrationChargeStatusCard({
  facility,
  facilityId,
  patientId,
}: RegistrationChargeStatusCardProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const registrationChargeDefinition =
    facility.registration_charge_item_definition;

  const { data, isPending } = useQuery({
    queryKey: ["registration-charge", facilityId, patientId],
    queryFn: query(chargeItemApi.listChargeItem, {
      pathParams: { facilityId },
      queryParams: {
        patient: patientId,
        limit: 1,
        service_resource: ChargeItemServiceResource.patient_registration,
        service_resource_id: patientId,
      },
    }),
    enabled: !!registrationChargeDefinition,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
  });

  if (!registrationChargeDefinition) {
    return null;
  }

  const chargeItem = data?.results[0];
  const invoiceStatus = chargeItem?.paid_invoice?.status;
  const isPaid = invoiceStatus === InvoiceStatus.balanced;
  const canDiscountChargeItem = !!chargeItem && !isPaid;
  const invoiceUrl = chargeItem?.paid_invoice
    ? `/facility/${facilityId}/billing/invoices/${chargeItem.paid_invoice.id}`
    : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wallet className="size-4 text-emerald-600" />
          {t("registration_charge", { defaultValue: "Registration charge" })}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium text-gray-950">
            {registrationChargeDefinition.title}
          </p>
          <p className="text-sm text-gray-600">
            {t("applies_to_newly_registered_patients_only", {
              defaultValue:
                "This applies only when the patient is first registered in this facility.",
            })}
          </p>
        </div>

        {isPending ? (
          <p className="text-sm text-gray-600">
            {t("loading_registration_charge_status", {
              defaultValue: "Loading registration charge status...",
            })}
          </p>
        ) : chargeItem ? (
          <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-start justify-between gap-3 max-sm:flex-col">
              <div>
                <p className="text-sm text-gray-600">
                  {t("amount", { defaultValue: "Amount" })}
                </p>
                <p className="text-lg font-semibold text-gray-950">
                  <MonetaryDisplay amount={chargeItem.total_price} />
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={isPaid ? "green" : "destructive"}>
                  {isPaid ? t("paid") : t("unpaid")}
                </Badge>
              </div>
            </div>

            <div className="space-y-1 text-sm text-gray-600">
              <p>
                {isPaid
                  ? t("registration_charge_paid_message", {
                      defaultValue:
                        "Payment has been recorded for this registration charge.",
                    })
                  : t("registration_charge_unpaid_message", {
                      defaultValue:
                        "This registration charge is still awaiting invoicing or payment.",
                    })}
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
            {t("no_registration_charge_for_patient", {
              defaultValue:
                "No registration charge was created for this patient. This usually means the patient was registered before the facility registration charge was configured.",
            })}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {canDiscountChargeItem ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditDialogOpen(true)}
            >
              {t("discount", { defaultValue: "Discount" })}
            </Button>
          ) : null}
          {invoiceUrl ? (
            <Button asChild variant="outline" size="sm">
              <Link href={invoiceUrl}>{t("invoice")}</Link>
            </Button>
          ) : null}
        </div>

        {chargeItem ? (
          <EditInvoiceDialog
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            facilityId={facilityId}
            chargeItems={[chargeItem]}
            onSuccess={() => {
              queryClient.invalidateQueries({
                queryKey: ["registration-charge", facilityId, patientId],
              });
            }}
            title={t("edit_charge_item")}
          />
        ) : null}
      </CardContent>
    </Card>
  );
}
